/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";

// Constants
const WHITE_COLOR = "white !important";

function SidenavCollapse({ icon, name, active, beta = false, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;

  return (
    <ListItem component="li" sx={{ overflow: 'visible' }}>
      <MDBox
        {...rest}
        sx={(theme) =>
          collapseItem(theme, {
            active,
            transparentSidenav,
            whiteSidenav,
            darkMode,
            sidenavColor,
          })
        }
      >
        <ListItemIcon
          sx={(theme) =>
            collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active })
          }
        >
          {typeof icon === "string" ? (
            <Icon sx={(theme) => collapseIcon(theme, { active })}>{icon}</Icon>
          ) : (
            icon
          )}
        </ListItemIcon>

        <MDBox display="flex" alignItems="center" flex={1} sx={{ minWidth: 0 }}>
          <ListItemText
            primary={name}
            sx={(theme) => ({
              ...collapseText(theme, {
                miniSidenav,
                transparentSidenav,
                whiteSidenav,
                active,
              }),
              "& .MuiListItemText-primary": {
                color: WHITE_COLOR,
              },
              "& .MuiTypography-root": {
                color: WHITE_COLOR,
              },
              flexShrink: 1,
              minWidth: 0,
            })}
          />
          {beta && (
            <MDBox
              sx={{
                backgroundColor: 'info.main',
                color: 'white',
                borderRadius: '12px',
                px: 1.5,
                py: 0.25,
                fontWeight: 600,
                fontSize: '0.75rem',
                boxShadow: '0 2px 8px rgba(52, 152, 219, 0.2)',
                textTransform: 'none',
                letterSpacing: 0.5,
                ml: 1,
                mr: 2,
                display: miniSidenav ? 'none' : 'block',
                flexShrink: 0,
                overflow: 'visible',
                position: 'relative',
                zIndex: 1
              }}
            >
              <MDTypography variant="caption" color="white" fontWeight="bold">
                BETA
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </ListItem>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  beta: PropTypes.bool,
};

export default SidenavCollapse;
